import { create } from 'zustand'

/**
 * Seed holds: the chip counts down the moment a spend starts.
 *
 * The real charge lands later, on the server, once the work succeeds. Until the
 * balance refetches, the chip shows the seeds as held so it never counts back
 * up mid build. A failed spend releases the hold.
 */
declare global {
  interface Window {
    SeedToss?: {
      toss: (o: {
        from: Element | DOMRect; to: Element | DOMRect; count: number
        onLaunch?: (i: number) => void; onLand?: (i: number) => void; onDone?: () => void
        theme?: 'light' | 'dark' | 'auto'; max?: number; bump?: boolean
      }) => void
    }
  }
}

interface SeedFx {
  held: number
  hold: (n: number) => void
  release: () => void
}

export const useSeedFx = create<SeedFx>((set) => ({
  held: 0,
  hold: (n) => set((s) => ({ held: s.held + n })),
  release: () => set({ held: 0 }),
}))

/**
 * Brian, Oct 22 (DEVELOPER-HANDOFF §3): when seeds are spent the chip shows a
 * short "−12" tick and nothing flies; the only animation is a coin unlock. So
 * a spend just holds the seeds off the chip until the server balance lands,
 * and the chip ticks. `to` is kept so existing callers need not change.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function tossSeeds(count: number, _to?: Element | null) {
  if (count <= 0) return
  useSeedFx.getState().hold(count)
}

/** Takes held seeds off the chip in the order the ledger spends them. */
export function applyHold(b: { weekly: number; gifts: number; purchased: number }, held: number) {
  let h = held
  const weekly = Math.max(0, b.weekly - h); h -= b.weekly - weekly
  const gifts = Math.max(0, b.gifts - h); h -= b.gifts - gifts
  const purchased = Math.max(0, b.purchased - h)
  return { weekly, gifts, purchased }
}
